// src/utils/itemMatch.js
// P4-1 (30 Oct 2026) - which Item Master item a line refers to, in ONE place.
//
// WHY THIS FILE EXISTS
// The store GRN (grnPlan.findMasterMatch) and the typed-name link on Direct PO /
// Amend PO (poLines.findExactNameMatch) each had their own matcher, and P4-2 /
// P4-3 (item create, pickers, BOQ) need the same rule. Item codes are NOT unique
// (dev: 451 of 646 items share a code) and one item_id can appear twice in a
// company's list (prod: ITEM-00004 = shared "" R44 Gas + own inactive Cutter
// Glender), so the rule is: never guess (D-309).
//   1. item_id - one hit wins; 2+ hits -> only the active ones; still not
//      exactly one -> ambiguous (never falls through to code / name)
//   2. legacy lines that stored the Firestore doc id (m.id)
//      a canonical id ("ITEM-..." / "custom_...") that is not found -> not_found
//   3. item_code - only if exactly ONE item has it
//   4. item name (itemNameKey) - only if exactly ONE item has it
// Pure (no Firebase) -> covered by src/utils/itemMatch.test.js (Jest).

const clean = (v) => String(v ?? "").trim();

// "Flexible Duct 250 mm  Dia" -> "flexible duct 250mm dia": lowercase, one space,
// a number joined to the unit after it. Also the P4-2 duplicate-name key.
export const itemNameKey = (name) =>
  String(name ?? "").toLowerCase().trim().replace(/\s+/g, " ").replace(/(\d)\s+([a-z])/g, "$1$2");

const isCanonicalId = (id) => id.startsWith("ITEM-") || id.startsWith("custom_");

/**
 * @param {Array}  masterList  Item Master items ({ id: docId, item_id, item_code, item_name, is_active, ... })
 * @param {Object} line        { item_id, item_code, item_name } - any of them may be missing
 * @returns {{ item: Object, how: "item_id"|"item_id_active"|"doc_id"|"item_code"|"item_name" }
 *         | { item: null, status: "ambiguous"|"not_found", candidates: Array }}
 */
export function matchItem(masterList, line = {}) {
  const list = (Array.isArray(masterList) ? masterList : []).filter(Boolean);
  const id   = clean(line?.item_id);
  const code = clean(line?.item_code);
  const name = itemNameKey(line?.item_name);
  let candidates = [];

  if (id) {
    const hits = list.filter((m) => clean(m.item_id) === id);
    if (hits.length === 1) return { item: hits[0], how: "item_id" };
    if (hits.length > 1) {
      const active = hits.filter((m) => m.is_active !== false);
      if (active.length === 1) return { item: active[0], how: "item_id_active" };
      return { item: null, status: "ambiguous", candidates: hits };
    }
    const byDocId = list.find((m) => clean(m.id) === id);
    if (byDocId) return { item: byDocId, how: "doc_id" };
    if (isCanonicalId(id)) return { item: null, status: "not_found", candidates: [] };
  }
  if (code) {
    const hits = list.filter((m) => clean(m.item_code) === code);
    if (hits.length === 1) return { item: hits[0], how: "item_code" };
    if (hits.length > 1) candidates = hits;
  }
  if (name) {
    const hits = list.filter((m) => itemNameKey(m.item_name) === name);
    if (hits.length === 1) return { item: hits[0], how: "item_name" };
    if (hits.length > 1) candidates = hits;
  }
  return candidates.length > 1
    ? { item: null, status: "ambiguous", candidates }
    : { item: null, status: "not_found", candidates: [] };
}

/**
 * P4-8: the ACTIVE Item Master item a line's item_id points to, for pre-filling
 * GST %, HSN, unit and code on a form. A disabled item never fills a form (prod
 * ITEM-00004 pre-filled from the inactive Cutter Glender), and two active items
 * on one id fill nothing. Matched by item_id only: never by code or name.
 *
 * @param {Array}  masterList  Item Master items, inactive included
 * @param {Object} line        { item_id } - may hold a legacy doc id
 * @returns {Object|null}
 */
export const activeMasterFor = (masterList, line = {}) => {
  const active = (Array.isArray(masterList) ? masterList : []).filter((m) => m && m.is_active !== false);
  return matchItem(active, { item_id: line?.item_id }).item;
};
